import { useEffect, useState } from 'react';
import {
  CreateWorkAssignmentReqData,
  WorkAssignment,
  useCreateWorkAssignment,
  useUpdateWorkAssignment,
  useWorkAssignments,
} from '../../hooks/workAssignmentHooks';
import { useGetPositionsForIncident } from 'hooks/incident/useRoster';
import { observability } from 'utils/observability';
import { useCurrentPeriod } from 'components/IAP/hooks/incidentHooks';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import WorkAssignmentRow from './WorkAssignmentTable/WorkAssignmentRow';
import {
  ResourceMainHeaderCells,
  ResourceSubHeaderCells,
} from './WorkAssignmentTable/ResourceHeaderCells';
import { useResourceState } from './WorkAssignmentTable/useResourceState';
import { WorkAssignmentDraft } from './WorkAssignmentTable/workAssignment.types';

type WorkAssignmentTableProps = {
  readOnly?: boolean;
  searchTerm?: string;
  hideAddButton?: boolean;
  adding?: boolean;
  onAddingChange?: (adding: boolean) => void;
};

const FIXED_LEFT = ['Work Group', 'Work Assignment Name'];
const FIXED_RIGHT = [
  '8. Overhead Positions',
  '9. Special Equipment & Supplies',
  '10. Reporting Location',
  '11. Requested Arrival Time',
  'Status',
  '',
];

const makeBlankRow = (id: string): WorkAssignment =>
  ({
    id,
    work_assignment_id: id,
    work_assignment: '',
    status: 'Planned',
    archived: false,
    strike_teams: [],
    resource_sets: [],
    leader: '',
    leader_contact: '',
    operational_period_number: 0,
    geolocation: null,
    resource_list: [],
  }) as unknown as WorkAssignment;

const WorkAssignmentTable: React.FC<WorkAssignmentTableProps> = ({
  readOnly = false,
  searchTerm = '',
  hideAddButton = false,
  adding: addingProp,
  onAddingChange,
}) => {
  const { data: workAssignments } = useWorkAssignments();
  const { data: roster } = useGetPositionsForIncident();
  const operationalPeriod = useCurrentPeriod();
  const updateWorkAssignment = useUpdateWorkAssignment();
  const createWorkAssignment = useCreateWorkAssignment();

  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [drafts, setDrafts] = useState<Record<string, WorkAssignmentDraft>>({});
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());
  const [newRows, setNewRows] = useState<WorkAssignment[]>([]);

  const resourceState = useResourceState(workAssignments);
  const {
    resourceColumns,
    addResource,
    deleteResource,
    getRowValues,
    setResourceValue,
    buildResourceList,
    computeTotals,
    hasStructuralChanges,
    hasEdits,
    reset: resetResources,
  } = resourceState;

  const workGroups =
    roster
      ?.filter((member) => member.is_workgroup)
      .map((wg) => ({ id: wg.id, name: wg.name })) ?? [];

  const periodNumber =
    (operationalPeriod?.currentPeriod?.period_number ?? 0) + 1;

  const filtered =
    workAssignments?.filter(
      (w) =>
        !w.archived &&
        !deletedIds.has(w.work_assignment_id) &&
        w.work_assignment.toLowerCase().includes(searchTerm.toLowerCase())
    ) ?? [];

  const handleFieldChange = (
    id: string,
    field: keyof WorkAssignment,
    val: unknown
  ) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: { ...(prev[id] ?? {}), [field]: val },
    }));
  };

  const handleDelete = (id: string) => {
    if (mode === 'edit') {
      setDeletedIds((p) => new Set(p).add(id));
      return;
    }
    // Saving if not in edit mode
    const original = workAssignments?.find((w) => w.work_assignment_id === id);
    if (!original) return;
    void updateWorkAssignment.mutateAsync({
      ...original,
      id: original.work_assignment_id,
      archived: true,
      operational_period_number: periodNumber,
    });
  };

  const handleDeleteNewRow = (id: string) => {
    setNewRows((prev) => prev.filter((r) => r.work_assignment_id !== id));
    setDrafts((prev) => {
      const { [id]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handleAddRow = () => {
    const tempId = `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setNewRows((prev) => [...prev, makeBlankRow(tempId)]);
    setMode('edit');
  };

  useEffect(() => {
    if (hideAddButton && addingProp) {
      handleAddRow();
      onAddingChange?.(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addingProp, hideAddButton]);

  const handleCancel = () => {
    setDrafts({});
    setDeletedIds(new Set());
    setNewRows([]);
    resetResources();
    setMode('view');
  };

  const handleSave = async () => {
    const byId = new Map(
      (workAssignments ?? []).map((w) => [w.work_assignment_id, w])
    );
    const edits: Promise<unknown>[] = [];
    const shouldUpdateAll = hasStructuralChanges;
    for (const wa of workAssignments ?? []) {
      if (deletedIds.has(wa.work_assignment_id)) continue;
      const draft = drafts[wa.work_assignment_id] ?? {};
      if (
        !shouldUpdateAll &&
        !drafts[wa.work_assignment_id] &&
        !hasEdits(wa.work_assignment_id)
      )
        continue;
      edits.push(
        updateWorkAssignment.mutateAsync({
          ...wa,
          ...draft,
          id: wa.work_assignment_id,
          resource_list: buildResourceList(wa),
          operational_period_number: periodNumber,
        })
      );
    }
    for (const id of deletedIds) {
      const original = byId.get(id);
      if (!original) continue;
      edits.push(
        updateWorkAssignment.mutateAsync({
          ...original,
          id: original.work_assignment_id,
          archived: true,
          operational_period_number: periodNumber,
        })
      );
    }
    for (const row of newRows) {
      const draft = drafts[row.work_assignment_id] ?? {};
      if (!draft.work_assignment) continue;
      const payload: CreateWorkAssignmentReqData = {
        work_assignment: draft.work_assignment ?? '',
        status: draft.status ?? 'Planned',
        parent_tactics: [],
        work_group_id: draft.work_group_id || undefined,
        overhead_positions: draft.overhead_positions,
        special_instructions: draft.special_instructions,
        reporting_location: draft.reporting_location,
        requested_arrival_time: draft.requested_arrival_time,
        resources: buildResourceList(row),
        operational_period_number: periodNumber,
      };
      edits.push(createWorkAssignment.mutateAsync(payload));
    }
    try {
      await Promise.all(edits);
    } catch (error) {
      observability.captureException(error, {
        context: 'WorkAssignmentTable.handleSave',
      });
      return;
    }
    setDrafts({});
    setDeletedIds(new Set());
    setNewRows([]);
    resetResources();
    setMode('view');
  };

  const isPending =
    updateWorkAssignment.isPending || createWorkAssignment.isPending;
  const totals = computeTotals([...filtered, ...newRows]);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)' }}>
      {!readOnly && (
        <div className="d-flex justify-content-end mb-2 gap-2">
          {!hideAddButton && (
            <StylishNewButton onClick={handleAddRow}>
              <SharedIcon iconName="add" classes="me-1" size="sm" />
              Add Work Assignment
            </StylishNewButton>
          )}
          {mode === 'view' && (
            <StylishNewButton
              className="button--secondary"
              onClick={() => setMode('edit')}
            >
              <SharedIcon iconName="edit" classes="me-1" size="sm" />
              Edit
            </StylishNewButton>
          )}
        </div>
      )}
      <div style={{ overflowX: 'auto', width: '100%' }}>
        <table className="v-top mb-0 table" style={{ width: 'max-content' }}>
          <thead>
            <tr>
              {FIXED_LEFT.map((c) => (
                <th key={c} rowSpan={2} style={{ minWidth: 160 }}>
                  {c}
                </th>
              ))}
              <ResourceMainHeaderCells
                resourceColumns={resourceColumns}
                readOnly={readOnly}
                onAddResource={addResource}
                onDeleteResource={deleteResource}
              />
              {FIXED_RIGHT.map((c, i) => (
                <th
                  key={c || `right-${i}`}
                  rowSpan={2}
                  style={{ minWidth: 140 }}
                >
                  {c}
                </th>
              ))}
            </tr>
            <tr>
              <ResourceSubHeaderCells
                resourceColumns={resourceColumns}
                readOnly={readOnly}
                onDeleteResource={deleteResource}
              />
            </tr>
          </thead>
          <tbody>
            {[...filtered, ...newRows].map((work) => (
              <WorkAssignmentRow
                key={work.work_assignment_id}
                work={work}
                draft={drafts[work.work_assignment_id]}
                mode={
                  newRows.some(
                    (r) => r.work_assignment_id === work.work_assignment_id
                  )
                    ? 'edit'
                    : mode
                }
                workGroups={workGroups}
                resourceColumns={resourceColumns}
                resourceValues={getRowValues(work)}
                readOnly={readOnly}
                onFieldChange={(field, val) =>
                  handleFieldChange(work.work_assignment_id, field, val)
                }
                onResourceChange={(name, key, val) =>
                  setResourceValue(work.work_assignment_id, name, key, val)
                }
                onDelete={() =>
                  newRows.some(
                    (r) => r.work_assignment_id === work.work_assignment_id
                  )
                    ? handleDeleteNewRow(work.work_assignment_id)
                    : handleDelete(work.work_assignment_id)
                }
              />
            ))}
          </tbody>
          {resourceColumns.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={FIXED_LEFT.length + 1} className="fw-bold">
                  <div className="d-flex flex-column gap-1">
                    <span style={{ fontSize: '0.85em' }}>
                      Total Resources Required
                    </span>
                    <span style={{ fontSize: '0.85em' }}>
                      Total Resources On Hand
                    </span>
                    <span style={{ fontSize: '0.85em' }}>
                      Total Resources Needed
                    </span>
                  </div>
                </td>
                {resourceColumns.map((name) => (
                  <td key={`${name}-total`}>
                    <div className="d-flex flex-column gap-1">
                      <span>{totals[name].required}</span>
                      <span>{totals[name].have}</span>
                      <span>{totals[name].need}</span>
                    </div>
                  </td>
                ))}
                <td colSpan={1 + FIXED_RIGHT.length} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {mode === 'edit' && !readOnly && (
        <div className="d-flex justify-content-end mt-3 gap-2">
          <StylishNewButton
            className="button--secondary"
            onClick={handleCancel}
          >
            Cancel
          </StylishNewButton>
          <StylishNewButton onClick={handleSave} disabled={isPending}>
            Save
          </StylishNewButton>
        </div>
      )}
    </div>
  );
};

export default WorkAssignmentTable;
